import { Controller } from "@hotwired/stimulus"
import { onHeaderSignup } from "../lib/header_signup"

const VIEW_2D = "2d"
const VIEW_3D = "3d"
const MESSAGE_READY = "fp:ready"
const MESSAGE_EDIT_ATTEMPT = "fp:editAttempt"
const MESSAGE_VIEW_SHOWN = "fp:viewShown"
const MESSAGE_WALK_STARTED = "fp:walkStarted"
const MESSAGE_WALK_ENDED = "fp:walkEnded"
const MESSAGE_WALK_POSE = "fp:walkPose"
const MESSAGE_POINTER_DOWN = "fp:pointerDown"
const MESSAGE_ESCAPE = "fp:escape"
// How long the demo popup stays up if nobody puts it away.
const DEMO_POPUP_MS = 10000
// How long to wait for the old drawing to say where the person in the walk-through stands before going on without it.
const WALK_POSE_TIMEOUT_MS = 800
// The first fit can land before the flat plan has its size back, so it is asked for again a moment later.
const FIT_AGAIN_MS = 300
const PAPER_CLEAR = "clear"
// While this is on the page, the first screen shows and the planner (bar, panel, canvas, footer) is out of sight.
const START_CLASS = "lp--start"
const CANOPY_OFF = "hidden"
const CANOPY_ON = "solid"
// A fresh plan is loaded behind the one showing and swapped in once it has drawn. In 3D that is when the frame says the scene is
// up; if it never does, it is swapped in anyway so the plan cannot get stuck behind a frame that is not coming.
const SWAP_FALLBACK_MS = 12000

// The Layout tab, full page: the guided flow (layout-guided) on the left, and here the canvas on the right — an iframe of the
// read-only floorplans embed (`?bridge=1`), driven by postMessage. The guided flow asks for a plan whenever an answer changes
// (`layout-guided:plan`); the 2D | 3D switch, the roof switch and the walk-through button (3D only; the roof is off to
// start with), zoom and the sign-up gate live here.
// It opens in 3D.
//
// The frame is only sent messages once it has said it is ready: the view the visitor chose while it was loading is remembered
// and sent the moment it is.
export default class extends Controller {
  static targets = ["frame", "empty", "loading", "twoD", "threeD", "viewToggle", "roofSwitch", "walkButton", "demoPopup", "gate", "gateSignup", "gateDismiss", "guidedPanel", "venuePanel", "paper"]
  static outlets = ["layout-guided", "layout-venue", "layout-start"]
  static values = { floorplansOrigin: String, defaults: Object }

  connect() {
    this.view = VIEW_3D
    this.options = { ...this.defaultsValue }
    this.frameReady = false
    this.walking = false
    this.pendingFrame = null
    this.swapTimer = null
    this.walkCarryingOver = false
    this.walkPoseWaiter = null
    this.venueMode = false

    this.onMessage = (event) => this.receive(event)
    window.addEventListener("message", this.onMessage)
    this.renderSwitches()
    this.onKeydown = (event) => { if (event.key === "Escape") this.escape() }
    document.addEventListener("keydown", this.onKeydown)
    // A press on this page, outside the popup, puts it away (a press inside the frame says so with fp:pointerDown).
    this.onDocumentPointerDown = (event) => { if (!this.demoPopupTarget.contains(event.target)) this.hideDemoPopup() }
    document.addEventListener("pointerdown", this.onDocumentPointerDown, true)
    // The header's "Sign up free" button opens the sign-up gate while the Layout tab is showing
    this.stopHeaderSignup = onHeaderSignup(this.element, () => this.openGate())
  }

  disconnect() {
    window.removeEventListener("message", this.onMessage)
    document.removeEventListener("keydown", this.onKeydown)
    document.removeEventListener("pointerdown", this.onDocumentPointerDown, true)
    clearTimeout(this.demoPopupTimer)
    this.stopHeaderSignup()
    this.dropPendingFrame()
  }

  // ── The canvas ──

  // Back to the first screen (a tent or a venue?), whichever flow the visitor was in. Both flows start again from their first question.
  startOver() {
    this.venueMode = false
    this.showPanel(this.guidedPanelTarget)
    this.resetStage()
    this.setView(VIEW_3D)
    this.layoutGuidedOutlet.start()
    this.element.classList.add(START_CLASS)
    this.layoutStartOutlet.start()
  }

  // "Start with a tent": the question panel takes over, at the first question.
  startTent() {
    this.venueMode = false
    this.showPanel(this.guidedPanelTarget)
    this.resetStage()
    this.layoutGuidedOutlet.start()
    this.leaveStartScreen()
  }

  // "Start with a venue": the venue panel takes over, and starts with a flat drawing to trace.
  showVenue() {
    this.venueMode = true
    this.showPanel(this.venuePanelTarget)
    this.resetStage()
    this.layoutVenueOutlet.start()
    this.leaveStartScreen()
  }

  // The two 3D drawings on the first screen are put away (a 3D scene is not free) while a flow runs.
  leaveStartScreen() {
    this.element.classList.remove(START_CLASS)
    this.layoutStartOutlet.stop()
  }

  showPanel(panel) {
    this.guidedPanelTarget.hidden = panel !== this.guidedPanelTarget
    this.venuePanelTarget.hidden = panel !== this.venuePanelTarget
  }

  resetStage() {
    this.hideDemoPopup()
    this.dropPendingFrame()
    this.walking = false
    this.frameTarget.hidden = true
    this.frameTarget.removeAttribute("src")
    this.loadingTarget.hidden = true
    this.emptyTarget.hidden = false
    this.frameReady = false
    this.setPaper({ detail: { state: PAPER_CLEAR } })
  }

  // The venue flow shows its example plan as a faded drawing (veiled), sweeps a scan line across it (scanning, over `durationMs`) and
  // then shows the drawing as it is (clear). It is all CSS on `data-state`; asking for the state it is already in changes nothing.
  setPaper(event) {
    const { state, durationMs } = event.detail
    if (state === this.paperTarget.dataset.state) return

    if (durationMs) this.paperTarget.style.setProperty("--trace-ms", `${durationMs}ms`)
    this.paperTarget.dataset.state = state
    this.paperTarget.hidden = state === PAPER_CLEAR
  }

  // The flow asks for a view (the flat drawing while it is traced, 3D from the walls on).
  setViewFrom(event) {
    this.setView(event.detail.view)
  }

  // The first plan fills the stage; every later one is loaded in a second frame behind the first and swapped in once it has
  // drawn, so the plan changes in place instead of blanking and reloading.
  showPlan(event) {
    const { embedUrl } = event.detail
    if (this.frameTarget.hidden || !this.frameTarget.getAttribute("src")) this.loadFirstPlan(embedUrl)
    else this.loadPendingFrame(embedUrl)
  }

  loadFirstPlan(embedUrl) {
    this.dropPendingFrame()
    this.frameReady = false
    this.emptyTarget.hidden = true
    this.loadingTarget.hidden = false
    this.frameTarget.hidden = false
    this.frameTarget.src = embedUrl
  }

  loadPendingFrame(embedUrl) {
    this.dropPendingFrame()
    const incoming = this.frameTarget.cloneNode(false)
    incoming.removeAttribute("data-layout-planner-target")
    incoming.classList.add("lg-stage__frame--incoming")
    incoming.src = embedUrl
    this.frameTarget.after(incoming)
    this.pendingFrame = incoming
  }

  dropPendingFrame() {
    clearTimeout(this.swapTimer)
    this.walkCarryingOver = false
    this.pendingFrame?.remove()
    this.pendingFrame = null
  }

  receive(event) {
    if (event.origin !== this.floorplansOriginValue) return

    if (this.pendingFrame && event.source === this.pendingFrame.contentWindow) return this.receiveFromPending(event.data)
    if (event.source !== this.frameTarget.contentWindow) return

    if (event.data.type === MESSAGE_READY) this.frameIsReady(event.data.summary)
    if (event.data.type === MESSAGE_VIEW_SHOWN) this.viewShown(event.data.view)
    if (event.data.type === MESSAGE_WALK_STARTED) this.walkChanged(true)
    if (event.data.type === MESSAGE_WALK_ENDED) this.walkChanged(false)
    if (event.data.type === MESSAGE_WALK_POSE) this.walkPoseWaiter?.(event.data.pose)
    if (event.data.type === MESSAGE_EDIT_ATTEMPT) this.showDemoPopup()
    if (event.data.type === MESSAGE_POINTER_DOWN) this.hideDemoPopup()
    if (event.data.type === MESSAGE_ESCAPE) this.escape()
  }

  // The first plan stays behind the loading veil until 3D is up (`fp:viewShown`), so the flat plan is never seen on the way in.
  frameIsReady(summary) {
    this.frameReady = true
    this.send("fp:setViewOptions", { ...this.options })
    if (this.view === VIEW_3D) this.send("fp:setView", { view: this.view })
    else this.loadingTarget.hidden = true
    this.tellGuided(summary)
  }

  // The flat plan was hidden while 3D showed, so it has to be fitted to the canvas again when it comes back.
  viewShown(view) {
    this.loadingTarget.hidden = true
    if (view !== VIEW_2D) return

    this.send("fp:zoom", { direction: "fit" })
    setTimeout(() => this.send("fp:zoom", { direction: "fit" }), FIT_AGAIN_MS)
  }

  receiveFromPending(message) {
    // A walk that was going on is carried into the new drawing: it is swapped in once the person is standing in it, so the visitor
    // never sees the orbit view in between. If the walk cannot start there, the new drawing is shown as it is.
    if (message.type === MESSAGE_WALK_STARTED) return this.swapIn(this.pendingFrame, { walking: true })
    if (message.type === MESSAGE_WALK_ENDED && this.walkCarryingOver) return this.swapIn(this.pendingFrame)
    if (message.type === MESSAGE_VIEW_SHOWN) return this.walkCarryingOver ? undefined : this.swapIn(this.pendingFrame)
    if (message.type !== MESSAGE_READY) return

    const incoming = this.pendingFrame
    const post = (type, payload = {}) => incoming.contentWindow?.postMessage({ type, ...payload }, this.floorplansOriginValue)
    post("fp:setViewOptions", { ...this.options })
    this.tellGuided(message.summary)
    if (this.walking) return void this.carryWalkInto(incoming, post)

    if (this.view === VIEW_3D) {
      post("fp:setView", { view: this.view })
      this.swapTimer = setTimeout(() => this.swapIn(incoming), SWAP_FALLBACK_MS)
    } else {
      this.swapIn(incoming)
    }
  }

  // The visitor is walking through the tent and has changed an answer: stand them in the same place in the new drawing.
  async carryWalkInto(incoming, post) {
    this.walkCarryingOver = true
    this.swapTimer = setTimeout(() => this.swapIn(incoming), SWAP_FALLBACK_MS)
    const pose = await this.requestWalkPose()
    if (incoming !== this.pendingFrame) return

    if (pose) {
      post("fp:walk", { on: true, pose })
    } else {
      // They stopped walking while the new drawing loaded: it comes in as the ordinary 3D view.
      this.walkCarryingOver = false
      post("fp:setView", { view: this.view })
    }
  }

  // Where the person in the walk-through stands in the drawing that is showing, or null if they are not walking (or it does not say).
  requestWalkPose() {
    return new Promise((resolve) => {
      const timer = setTimeout(() => { this.walkPoseWaiter = null; resolve(null) }, WALK_POSE_TIMEOUT_MS)
      this.walkPoseWaiter = (pose) => { clearTimeout(timer); this.walkPoseWaiter = null; resolve(pose) }
      this.send("fp:getWalkPose")
    })
  }

  swapIn(incoming, { walking = false } = {}) {
    if (!incoming || incoming !== this.pendingFrame) return

    clearTimeout(this.swapTimer)
    this.walkCarryingOver = false
    this.frameTarget.remove()
    incoming.setAttribute("data-layout-planner-target", "frame")
    incoming.classList.remove("lg-stage__frame--incoming")
    this.pendingFrame = null
    // The frame that is walking gets the keys; one that is not is just a plan again.
    this.walkChanged(walking)
  }

  // The numbers the frame drew go to the panel that asked for the plan.
  tellGuided(summary) {
    if (!summary) return

    const panels = this.venueMode ? this.layoutVenueOutlets : this.layoutGuidedOutlets
    panels.forEach((panel) => panel.summary(summary))
  }

  // Dropped until the frame is ready; `frameIsReady` replays the chosen view.
  send(type, payload = {}) {
    if (!this.frameReady) return

    this.frameTarget.contentWindow?.postMessage({ type, ...payload }, this.floorplansOriginValue)
  }

  // ── The 2D | 3D switch and zoom ──

  show2d() { this.setView(VIEW_2D) }
  show3d() { this.setView(VIEW_3D) }
  zoomIn() { this.send("fp:zoom", { direction: "in" }) }
  zoomOut() { this.send("fp:zoom", { direction: "out" }) }

  setView(view) {
    this.view = view
    this.renderSwitches()
    this.send("fp:setView", { view })
    if (view === VIEW_3D) this.send("fp:setViewOptions", { ...this.options })
  }

  // ── The walk-through ──

  // A person at the end of the tent, seen from eye height. Esc, or the Exit button inside the view, ends it.
  startWalk() {
    this.send("fp:walk", { on: true })
  }

  walkChanged(walking) {
    this.walking = walking
    this.renderSwitches()
    // Keys go to the frame that is walking.
    if (walking) {
      this.frameTarget.focus()
      this.frameTarget.contentWindow?.focus()
    }
  }

  // ── The roof ──

  // Off to start with (the canopy hidden), so the inside of the tent can be seen; on is the solid canopy.
  toggleRoof() {
    this.options = { ...this.options, canopy: this.roofOn ? CANOPY_OFF : CANOPY_ON }
    this.renderSwitches()
    this.send("fp:setViewOptions", { ...this.options })
  }

  get roofOn() {
    return this.options.canopy !== CANOPY_OFF
  }

  renderSwitches() {
    this.twoDTarget.classList.toggle("lg-toggle__button--on", this.view === VIEW_2D)
    this.threeDTarget.classList.toggle("lg-toggle__button--on", this.view === VIEW_3D)
    // The walk-through has its own banner, Exit button and map across the top of the canvas, so these step aside while it runs.
    // A venue has no roof to take off.
    this.roofSwitchTarget.hidden = this.view !== VIEW_3D || this.walking || this.venueMode
    this.walkButtonTarget.hidden = this.view !== VIEW_3D || this.walking
    this.viewToggleTarget.hidden = this.walking
    this.roofSwitchTarget.setAttribute("aria-checked", String(this.roofOn))
    this.roofSwitchTarget.classList.toggle("lp-roof--on", this.roofOn)
  }

  // ── The demo popup ──

  // Somebody tried to move or delete something on the plan. It is a demo, so nothing happens: this says why, and how to get the real thing.
  // Not a dialog that takes the screen: a card over the bottom of the canvas that the visitor can ignore, put away, or act on. It does not
  // take focus, so a walk-through (which is not editing, and never shows it) keeps its keys.
  showDemoPopup() {
    if (this.walking || !this.gateTarget.hidden) return

    this.demoPopupTarget.hidden = false
    clearTimeout(this.demoPopupTimer)
    this.demoPopupTimer = setTimeout(() => this.hideDemoPopup(), DEMO_POPUP_MS)
  }

  hideDemoPopup() {
    clearTimeout(this.demoPopupTimer)
    this.demoPopupTarget.hidden = true
  }

  signUpFromPopup() {
    this.hideDemoPopup()
    this.openGate()
  }

  // Esc puts away the popup and the sign-up gate, whichever is up.
  escape() {
    this.hideDemoPopup()
    this.closeGate()
  }

  // ── The sign-up gate ──

  openGate() {
    if (!this.gateTarget.hidden) return

    this.gateOpener = document.activeElement
    this.gateTarget.hidden = false
    this.gateSignupTarget.focus()
  }

  closeGate() {
    if (this.gateTarget.hidden) return

    this.gateTarget.hidden = true
    this.gateOpener?.focus?.()
  }

  // Tab stays inside the dialog.
  gateKeydown(event) {
    if (event.key !== "Tab") return

    const focusable = [this.gateSignupTarget, this.gateDismissTarget, ...this.gateTarget.querySelectorAll("a[href]:not(.fp-demo__gate-primary)")]
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }
}
